import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import jsQR from "jsqr";
import { useAuth } from "../context/authContext";
import { permisoDesactivado, setPermisoDesactivado } from "../utils/permisosApp";

// FICHAJE POR QR + UBICACIÓN (supervisoras y asistentas, de momento solo en pruebas).
// Siempre se puede fichar. El servidor compara el punto con el del piso y guarda la distancia.
//  · Todo bien (dentro de límites)            → se guarda y sale en verde 2 s (se cierra solo).
//  · QR que no existe / fuera de límites / sin ubicación / cambio de piso → alerta de otro color y hay que CONFIRMAR.
//  · «Este piso no está asociado a ti»        → se ficha igual, con aviso.
// Mientras la persona está fichada en un piso, toda la app se tiñe de verde y abajo sale «EN {ABREVIATURA}» con el botón de salida.
// Para abrirlo desde otra pantalla: window.dispatchEvent(new Event("fichaje:abrir")) dentro del handler del toque (iOS lo exige).

const ROLES = ["supervisora", "asistenta"];
const ROLES_CON_BOTON_FLOTANTE = [];       // supervisora y asistenta tienen su propio botón «Fichar» en su panel (mismo estilo)
const hhmm = (iso) => (iso ? iso.slice(11, 16) : "");
const fmtDur = (min) => {
  if (min == null || min < 1) return "menos de 1 min";
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h} h${m ? ` ${m} min` : ""}` : `${m} min`;
};
// minutos desde una hora de Madrid «YYYY-MM-DDTHH:MM:SS» (el navegador de la persona está en la misma zona). Cálculo local: no pide nada al servidor.
const minDesde = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000)) : null);

// ── Hora de salida manual: día / hora / minutos entre la entrada y ahora ──────────────
const pad = (n) => String(n).padStart(2, "0");
const isoDia = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const nombreDia = (dia) => {
  const h = new Date(); const ayer = new Date(); ayer.setDate(h.getDate() - 1);
  return dia === isoDia(h) ? "hoy" : dia === isoDia(ayer) ? "ayer" : `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;
};
const valorAIso = (v) => `${v.dia}T${pad(v.h)}:${pad(v.m)}`;
// rango permitido: después de la entrada (minuto siguiente) y hasta el minuto actual
const rangoHoras = (desde, dia) => {
  const ahora = new Date();
  const dEnt = desde.slice(0, 10), hEnt = Number(desde.slice(11, 13)), mEnt = Number(desde.slice(14, 16));
  const min = dia === dEnt ? (mEnt >= 59 ? hEnt + 1 : hEnt) : 0;
  const max = dia === isoDia(ahora) ? ahora.getHours() : 23;
  return [min, max];
};
const rangoMin = (desde, dia, h) => {
  const ahora = new Date();
  const dEnt = desde.slice(0, 10), hEnt = Number(desde.slice(11, 13)), mEnt = Number(desde.slice(14, 16));
  const min = dia === dEnt && h === hEnt ? mEnt + 1 : 0;
  const max = dia === isoDia(ahora) && h === ahora.getHours() ? ahora.getMinutes() : 59;
  return [min, max];
};
const ajustaHora = (desde, v) => {
  const [h0, h1] = rangoHoras(desde, v.dia);
  const h = Math.min(Math.max(v.h, h0), h1);
  const [m0, m1] = rangoMin(desde, v.dia, h);
  return { dia: v.dia, h, m: Math.min(Math.max(v.m, m0), m1) };
};
const horaInicial = (desde) => { const a = new Date(); return ajustaHora(desde, { dia: isoDia(a), h: a.getHours(), m: a.getMinutes() }); };
const horaValida = (desde, v) => !!v && valorAIso(v) > desde.slice(0, 16) && new Date(valorAIso(v)).getTime() <= Date.now();
const minEntre = (desde, v) => Math.max(0, Math.round((new Date(valorAIso(v)).getTime() - new Date(desde).getTime()) / 60000));

function SelectorHora({ desde, valor, onChange }) {
  const hoy = isoDia(new Date());
  const dias = [];
  for (let d = new Date(desde.slice(0, 10) + "T00:00:00"); isoDia(d) <= hoy && dias.length < 60; d.setDate(d.getDate() + 1)) dias.push(isoDia(d));
  const [h0, h1] = rangoHoras(desde, valor.dia);
  const [m0, m1] = rangoMin(desde, valor.dia, valor.h);
  const horas = []; for (let h = h0; h <= h1; h++) horas.push(h);
  const minutos = []; for (let m = m0; m <= m1; m++) minutos.push(m);
  const cambia = (cambio) => onChange(ajustaHora(desde, { ...valor, ...cambio }));
  if (!horas.length) return <div style={st.avisoAmbar}>Has entrado hace menos de un minuto: espera un poco para poner la salida.</div>;
  return (
    <div>
      {dias.length > 1 && (
        <select value={valor.dia} onChange={(e) => cambia({ dia: e.target.value })} style={{ ...st.selDia }}>
          {dias.map((d) => <option key={d} value={d}>📅 {nombreDia(d)} ({d.slice(8, 10)}/{d.slice(5, 7)})</option>)}
        </select>
      )}
      <div style={st.relojFila}>
        <div style={st.relojCol}>
          <div style={st.relojLbl}>Hora</div>
          <select value={valor.h} onChange={(e) => cambia({ h: Number(e.target.value) })} style={st.selHora}>{horas.map((h) => <option key={h} value={h}>{pad(h)}</option>)}</select>
        </div>
        <div style={st.relojPuntos}>:</div>
        <div style={st.relojCol}>
          <div style={st.relojLbl}>Minutos</div>
          <select value={valor.m} onChange={(e) => cambia({ m: Number(e.target.value) })} style={st.selHora}>{minutos.map((m) => <option key={m} value={m}>{pad(m)}</option>)}</select>
        </div>
      </div>
      <div style={{ fontSize: 12.5, color: "#64748b", textAlign: "center", marginTop: 8 }}>Solo se puede elegir entre tu entrada ({hhmm(desde)}) y ahora.</div>
      <div style={st.relojResumen}>Saldrás a las <b>{pad(valor.h)}:{pad(valor.m)}</b>{valor.dia !== hoy ? ` (${nombreDia(valor.dia)})` : ""} · habrás estado <b>{fmtDur(minEntre(desde, valor))}</b></div>
    </div>
  );
}

const TEXTOS_ALERTA = (r) => {
  const piso = r.piso?.abreviatura || "el piso";
  const l = [];
  if (r.cambio_de_piso) l.push({ t: "No has desfichado del piso anterior", d: `Entraste en ${r.piso_anterior?.abreviatura} ${nombreDia((r.anterior_desde || "").slice(0, 10))} a las ${hhmm(r.anterior_desde)}. Pon una hora aproximada de salida y se registrará tu entrada en ${piso}.`, c: "ambar" });
  if (r.estado === "qr_desconocido") l.push({ t: "QR no reconocido", d: r.tipo === "salida" ? `Este QR no corresponde a ningún piso (estás en ${piso}).` : "Este QR no corresponde a ningún piso.", c: "rojo" });
  else if (r.estado === "fuera") l.push({ t: "Fuera de límites", d: `Estás a ${Math.round(r.distancia_m)} m de ${piso} (margen ${Math.round(r.margen_m)} m).`, c: "rojo" });
  if (r.aviso_no_asociado) l.push({ t: "Este piso no está asociado a ti", d: "", c: "ambar" });
  return l;
};

// Por qué no hay ubicación (se guarda en el fichaje): 1 permiso denegado · 2 no disponible · 3 tiempo agotado · 0 sin API de ubicación
const motivoDe = (pos) => (pos && pos.lat != null ? null : pos?.error === 1 ? "denegada" : pos?.error === 2 ? "no_disponible" : pos?.error === 0 ? "sin_soporte" : "tiempo_agotado");

// Si al fichar no había ubicación, se sigue buscando EN SEGUNDO PLANO hasta 30 s sin avisar a la persona; si llega, se manda al fichaje
// (el servidor pasa el estado a dentro/fuera y conserva el motivo). No se reintenta si el permiso está denegado.
// Si en esos 30 s no llegó nada (típico: se bloqueó el móvil o se cambió de app y el navegador pausó el GPS), se vuelve a intentar al
// volver a la app, mientras el servidor aún lo acepte (10 min tras fichar).
const VENTANA_REINTENTO_MS = 9 * 60 * 1000;
let ubicPendiente = null;      // {id, t0}: fichaje al que aún le falta la ubicación
let ubicEnCurso = false;
const reintentarUbicacion = (idFichaje) => {
  if (!idFichaje || !("geolocation" in navigator) || ubicEnCurso) return;
  if (!ubicPendiente || ubicPendiente.id !== idFichaje) ubicPendiente = { id: idFichaje, t0: Date.now() };
  ubicEnCurso = true;
  let mejor = null, id = null, t = null, hecho = false;
  const fin = async () => {
    if (hecho) return;
    hecho = true;
    clearTimeout(t);
    if (id != null) navigator.geolocation.clearWatch(id);
    ubicEnCurso = false;
    if (!mejor) return;                                   // sigue pendiente: se reintenta al volver a la app
    try {
      await axios.post("/api/fichajes/ubicacion", { id_fichaje: idFichaje, lat: mejor.lat, lng: mejor.lng, accuracy: mejor.accuracy });
      if (ubicPendiente?.id === idFichaje) ubicPendiente = null;
    } catch (e) {
      if (e.response && ubicPendiente?.id === idFichaje) ubicPendiente = null;      // el servidor la rechazó (ya no procede): no insistir
    }
  };
  id = navigator.geolocation.watchPosition(
    (p) => { const l = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }; if (!mejor || l.accuracy < mejor.accuracy) mejor = l; if (l.accuracy <= 50) fin(); },
    (e) => { if (e.code === 1) { if (ubicPendiente?.id === idFichaje) ubicPendiente = null; fin(); } },
    { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 }
  );
  t = setTimeout(fin, 30000);
};
const reintentarPendiente = () => {
  if (!ubicPendiente) return;
  if (Date.now() - ubicPendiente.t0 > VENTANA_REINTENTO_MS) { ubicPendiente = null; return; }
  reintentarUbicacion(ubicPendiente.id);
};

// Pantallas donde la barra de abajo estorba (botones fijos propios): login, crear compras y carrito. Otras pantallas la ocultan
// a ratos (p. ej. el formulario de añadir/editar préstamo) con window.dispatchEvent(new CustomEvent("fichaje:ocultar", { detail: true|false }))
const RUTAS_SIN_BARRA = /^\/(login|supervisora\/(pisoCrearCompras|crearCompras|carrito))(\/|$)/;

const FichajePiso = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [ocultoPantalla, setOcultoPantalla] = useState(false);
  useEffect(() => {
    const h = (e) => setOcultoPantalla(!!(e && e.detail));
    window.addEventListener("fichaje:ocultar", h);
    return () => window.removeEventListener("fichaje:ocultar", h);
  }, []);
  const sinBarra = RUTAS_SIN_BARRA.test(pathname) || ocultoPantalla;
  const activo = !!user && ROLES.includes(user.tipo);
  const [enPiso, setEnPiso] = useState(null);                 // {id_piso, abreviatura, desde}
  const [abierto, setAbierto] = useState(false);
  const [fase, setFase] = useState("escaneando");              // escaneando | enviando | ok | confirmar | error
  const [resultado, setResultado] = useState(null);
  const [camError, setCamError] = useState("");
  const [horaSel, setHoraSel] = useState(null);                // {dia, h, m} salida manual elegida
  const [errManual, setErrManual] = useState("");
  const [cargandoUbic, setCargandoUbic] = useState(false);      // «Cargando ubicación… espera unos segundos» mientras el GPS no responde
  const [geo, setGeo] = useState({ estado: "idle", acc: null });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const intentoRef = useRef(1);      // 2 si ya se repitió la lectura de ubicación en este fichaje (también al confirmar después)
  const posRef = useRef(null);       // promesa con la ubicación
  const precalRef = useRef(null);    // temporizador que apaga la cámara precalentada si no se llega a abrir el fichaje
  const [camLista, setCamLista] = useState(false);
  const camRef = useRef(null);       // promesa con la cámara, pedida en el mismo toque que abre el fichaje (sin esperar al render)
  const qrRef = useRef("");
  const cierreRef = useRef(null);

  useEffect(() => {
    if (!activo) return undefined;
    const alVolver = () => { if (!document.hidden) reintentarPendiente(); };
    document.addEventListener("visibilitychange", alVolver);
    window.addEventListener("focus", alVolver);
    return () => { document.removeEventListener("visibilitychange", alVolver); window.removeEventListener("focus", alVolver); };
  }, [activo]);

  const cargarEstado = useCallback(() => {
    axios.get("/api/fichajes/estado").then((r) => setEnPiso(r.data?.en_piso || null)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!activo) { setEnPiso(null); return undefined; }
    cargarEstado();
    window.addEventListener("focus", cargarEstado);
    return () => window.removeEventListener("focus", cargarEstado);
  }, [activo, cargarEstado]);

  // «lleva X»: se repinta SOLO esa línea, una vez por minuto justo al cambiar el minuto, sin red y parado con la pantalla apagada
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!enPiso) return undefined;
    let t = null;
    const programar = () => {
      t = setTimeout(() => { if (!document.hidden) setTick((n) => n + 1); programar(); }, 60000 - (Date.now() % 60000) + 50);
    };
    const alVolver = () => { if (!document.hidden) setTick((n) => n + 1); };
    programar();
    document.addEventListener("visibilitychange", alVolver);
    return () => { clearTimeout(t); document.removeEventListener("visibilitychange", alVolver); };
  }, [enPiso]);

  // Dejar sitio al aviso inferior mientras se está en un piso: se mide su altura real (cambia si el nombre ocupa 2 líneas)
  // y se publica en --fichaje-h para que los botones fijos de abajo de otras pantallas se coloquen por encima
  const barraRef = useRef(null);
  useEffect(() => {
    if (!enPiso || sinBarra) return undefined;
    const prev = document.body.style.paddingBottom;
    const poner = () => {
      const h = barraRef.current ? Math.ceil(barraRef.current.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty("--fichaje-h", `${h}px`);
      document.body.style.paddingBottom = `${h + 8}px`;
    };
    poner();
    const ro = typeof ResizeObserver !== "undefined" && barraRef.current ? new ResizeObserver(poner) : null;
    if (ro) ro.observe(barraRef.current);
    window.addEventListener("resize", poner);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", poner);
      document.documentElement.style.removeProperty("--fichaje-h");
      document.body.style.paddingBottom = prev;
    };
  }, [enPiso, sinBarra]);

  const pararCamara = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const cerrar = useCallback(() => {
    clearTimeout(cierreRef.current);
    if (camRef.current) { camRef.current.then((s) => s.getTracks().forEach((t) => t.stop())).catch(() => {}); camRef.current = null; }
    pararCamara();
    setAbierto(false);
    setResultado(null);
    setFase("escaneando");
    setCamError("");
  }, [pararCamara]);

  // Cámara: 1280×720 (un QR impreso pequeño o lejos necesita más resolución que 960×540)
  const pedirCamara = () => navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });

  // Ubicación: se pide en el mismo gesto del toque (iOS Safari lo exige)
  const posDesdeRef = useRef(0);     // cuándo se pidió la última ubicación (se reutiliza si se pidió al tocar el botón)
  const pedirUbicacion = (fresca) => {
    posDesdeRef.current = Date.now();
    if (!("geolocation" in navigator)) {
      posRef.current = Promise.resolve({ error: 0 });
      setGeo({ estado: "error", acc: null });
      return;
    }
    setGeo({ estado: "buscando", acc: null });
    // Se escucha el GPS y se da por buena la primera lectura con precisión ≤ 25 m; si no llega, la mejor que haya a los 6 s (y como tope 12 s).
    // Una lectura en caché de ≤ 5 s cuenta (andando son ~8 m). `fresca` = segunda lectura tras salir «fuera»: sin caché y esperando hasta 8 s.
    posRef.current = new Promise((resolve) => {
      let mejor = null, id = null, t1 = null, t2 = null, hecho = false;
      const fin = (r) => {
        if (hecho) return;
        hecho = true;
        clearTimeout(t1); clearTimeout(t2);
        if (id != null) navigator.geolocation.clearWatch(id);
        if (r.lat != null) setGeo({ estado: "ok", acc: Math.round(r.accuracy) });
        else setGeo({ estado: r.error === 1 ? "denegada" : "error", acc: null });
        resolve(r);
      };
      const lectura = (p) => ({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy });
      id = navigator.geolocation.watchPosition(
        (p) => { const l = lectura(p); if (!mejor || l.accuracy < mejor.accuracy) mejor = l; if (l.accuracy <= 25) fin(l); },
        (e) => { if (e.code === 1 || !mejor) fin(mejor || { error: e.code }); },
        { enableHighAccuracy: true, maximumAge: fresca ? 0 : 5000, timeout: 12000 }
      );
      t1 = setTimeout(() => { if (mejor) fin(mejor); }, fresca ? 8000 : 6000);
      t2 = setTimeout(() => fin(mejor || { error: 3 }), 12000);
    });
  };

  const abrir = () => {
    if (permisoDesactivado("ubicacion") || permisoDesactivado("camara")) {      // los apagó la persona en Permisos: no se ficha
      setResultado(null); setCamError(""); setFase("desactivado"); setAbierto(true);
      return;
    }
    if (!posRef.current || Date.now() - posDesdeRef.current > 8000) pedirUbicacion();      // lo primero, antes de cualquier setState (si ya se pidió al tocar, se reutiliza)
    clearTimeout(precalRef.current);
    if (!camRef.current && navigator.mediaDevices?.getUserMedia) { camRef.current = pedirCamara(); camRef.current.catch(() => {}); }   // en paralelo con la ubicación (ya está pedida si se precalentó al tocar)
    setCamLista(false);
    setResultado(null); setCamError(""); setFase("escaneando"); setAbierto(true);
  };
  const abrirRef = useRef(abrir);
  abrirRef.current = abrir;
  // Al TOCAR el botón (antes de soltarlo) ya se empieza a encender la cámara; si no llega a abrirse el fichaje, se apaga a los 6 s
  useEffect(() => {
    if (!activo) return undefined;
    const h = () => {
      if (permisoDesactivado("ubicacion") || permisoDesactivado("camara")) return;
      if (!posRef.current || Date.now() - posDesdeRef.current > 8000) pedirUbicacion();       // el GPS empieza a buscar ya, al tocar «Fichar»
      if (camRef.current || !navigator.mediaDevices?.getUserMedia) return;
      const p = pedirCamara();
      p.catch(() => {});
      camRef.current = p;
      clearTimeout(precalRef.current);
      precalRef.current = setTimeout(() => {
        if (camRef.current === p) { p.then((s) => s.getTracks().forEach((t) => t.stop())).catch(() => {}); camRef.current = null; }
      }, 6000);
    };
    window.addEventListener("fichaje:precalentar", h);
    return () => { window.removeEventListener("fichaje:precalentar", h); clearTimeout(precalRef.current); };
  }, [activo]); // eslint-disable-line react-hooks/exhaustive-deps
  // Calentar el GPS: al entrar en la app (y al volver a ella, como mucho cada 3 min) se escucha la ubicación 40 s sin usarla, para que el
  // primer fichaje no pille el GPS en frío. No pide permisos nuevos si ya están desactivados o denegados.
  useEffect(() => {
    if (!activo || !("geolocation" in navigator)) return undefined;
    let id = null, t = null, ultimo = 0;
    const parar = () => { clearTimeout(t); if (id != null) navigator.geolocation.clearWatch(id); id = null; };
    const calentar = () => {
      if (document.visibilityState === "hidden" || id != null || Date.now() - ultimo < 3 * 60 * 1000) return;
      if (permisoDesactivado("ubicacion")) return;
      ultimo = Date.now();
      id = navigator.geolocation.watchPosition(() => {}, (e) => { if (e.code === 1) parar(); }, { enableHighAccuracy: true, maximumAge: 0, timeout: 40000 });
      t = setTimeout(parar, 40000);
    };
    calentar();
    const alVolver = () => { if (document.visibilityState === "visible") calentar(); else parar(); };
    document.addEventListener("visibilitychange", alVolver);
    return () => { document.removeEventListener("visibilitychange", alVolver); parar(); };
  }, [activo]);
  useEffect(() => {
    if (!activo) return undefined;
    const h = () => abrirRef.current();
    window.addEventListener("fichaje:abrir", h);
    return () => window.removeEventListener("fichaje:abrir", h);
  }, [activo]);

  // Espera la ubicación hasta 10 s; si tarda más de medio segundo se enseña «Cargando ubicación…». Si no llega, se ficha igual y se sigue buscando en segundo plano.
  const esperarUbicacion = async () => {
    const pp = posRef.current || Promise.resolve({ error: 0 });
    let lista = false;
    pp.then(() => { lista = true; });
    const tm = setTimeout(() => { if (!lista) setCargandoUbic(true); }, 500);
    const pos = await Promise.race([pp, new Promise((r) => setTimeout(() => r({ error: 3 }), 10000))]);
    clearTimeout(tm);
    setCargandoUbic(false);
    return pos;
  };

  const enviar = useCallback(async (qr, confirmar, horaAnterior, intento = 1, posPrevia = null) => {
    setFase("enviando");
    if (!confirmar && intento === 1) intentoRef.current = 1;
    try {
      let pos = await esperarUbicacion();
      if (intento === 2 && !(pos && pos.lat != null)) pos = posPrevia;      // la segunda lectura no llegó: se reenvía la primera
      const cuerpo = { qrdata: qr, confirmar: !!confirmar };
      if (horaAnterior) cuerpo.hora_salida_anterior = horaAnterior;
      if (pos && pos.lat != null) { cuerpo.lat = pos.lat; cuerpo.lng = pos.lng; cuerpo.accuracy = pos.accuracy; cuerpo.intentos = Math.max(intento, intentoRef.current); }
      else cuerpo.motivo_ubicacion = motivoDe(pos);
      const { data } = await axios.post("/api/fichajes/fichar", cuerpo);
      if (data.requiere_confirmacion && data.estado === "fuera" && intento === 1 && pos && pos.lat != null) {
        // sale «fuera»: puede ser una lectura mala (wifi/antena que se da por precisa): se repite una vez, sin caché, antes de preguntar
        intentoRef.current = 2;
        pedirUbicacion(true);
        return enviar(qr, confirmar, horaAnterior, 2, pos);
      }
      setResultado(data);
      if (data.requiere_confirmacion) {
        if (data.cambio_de_piso && data.anterior_desde) setHoraSel(horaInicial(data.anterior_desde));
        setFase("confirmar");
        return;
      }
      setFase("ok");
      if (data.en_piso !== undefined) setEnPiso(data.en_piso);
      if (!(pos && pos.lat != null) && pos?.error !== 1) reintentarUbicacion(data.id_fichaje);
      const largo = data.aviso_no_asociado || (data.estado !== "dentro" && data.estado !== "sin_ubicacion") || data.tipo === "salida";      // la salida enseña el tiempo: algo más de margen para leerlo
      cierreRef.current = setTimeout(cerrar, largo ? 3500 : 2000);
    } catch (e) {
      setResultado({ mensaje: e.response?.data?.error || "No se pudo fichar. Inténtalo de nuevo." });
      setFase("error");
    }
  }, [cerrar]);

  // «Se me olvidó desfichar»: cierra la entrada abierta con la hora elegida
  const abrirManual = () => {
    if (!enPiso) return;
    pararCamara();
    pedirUbicacion();                // se guarda también dónde está ahora (en el mismo toque, iOS lo exige)
    setErrManual(""); setHoraSel(horaInicial(enPiso.desde)); setFase("manual");
  };
  const guardarManual = async () => {
    if (!enPiso || !horaValida(enPiso.desde, horaSel)) return;
    setErrManual(""); setFase("enviando");
    try {
      const pos = await esperarUbicacion();
      const cuerpo = { fecha_hora: valorAIso(horaSel) };
      if (pos && pos.lat != null) { cuerpo.lat = pos.lat; cuerpo.lng = pos.lng; cuerpo.accuracy = pos.accuracy; }
      else cuerpo.motivo_ubicacion = motivoDe(pos);
      const { data } = await axios.post("/api/fichajes/salida_manual", cuerpo);
      if (!(pos && pos.lat != null) && pos?.error !== 1) reintentarUbicacion(data.id_fichaje);
      setResultado(data);
      setEnPiso(null);
      setFase("ok");
      cierreRef.current = setTimeout(cerrar, 3500);
    } catch (e) {
      setErrManual(e.response?.data?.error || "No se pudo guardar la salida. Inténtalo de nuevo.");
      setFase("manual");
    }
  };

  // Cámara + lectura del QR
  useEffect(() => {
    if (!abierto || fase !== "escaneando") return undefined;
    let cancelado = false;
    let n = 0;
    let leido = false;
    let ocupado = false;
    let nativoFalla = false;
    // Lector nativo del navegador (BarcodeDetector: Chrome/Android) a resolución completa; jsQR queda de respaldo (y de apoyo
    // cada pocos fotogramas) a mayor resolución que antes y también con QR invertidos (claro sobre oscuro).
    let nativo = null;
    try { if ("BarcodeDetector" in window) nativo = new window.BarcodeDetector({ formats: ["qr_code"] }); } catch (_) { nativo = null; }
    const leer = (texto) => {
      if (leido || cancelado || !texto) return;
      leido = true;
      if (navigator.vibrate) navigator.vibrate(120);
      qrRef.current = String(texto).trim();
      pararCamara();
      enviar(qrRef.current, false);
    };
    const tick = () => {
      const video = videoRef.current, canvas = canvasRef.current;
      if (cancelado || leido || !video || !canvas) return;
      const listo = video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth;
      if (listo && nativo && !nativoFalla && !ocupado) {
        ocupado = true;
        nativo.detect(video)
          .then((codigos) => { ocupado = false; const c = (codigos || []).find((x) => x.rawValue); if (c) leer(c.rawValue); })
          .catch(() => { ocupado = false; nativoFalla = true; });
      }
      // jsQR: cada fotograma si no hay lector nativo (o falló); si lo hay, solo como apoyo cada 4
      if (listo && (!nativo || nativoFalla ? n % 2 === 0 : n % 4 === 0)) {
        const esc = Math.min(1, 960 / video.videoWidth);
        canvas.width = Math.round(video.videoWidth * esc); canvas.height = Math.round(video.videoHeight * esc);
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "attemptBoth" });
        if (code && code.data) { leer(code.data); return; }
      }
      n++;
      rafRef.current = requestAnimationFrame(tick);
    };
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setCamError("Este navegador no permite usar la cámara (hace falta HTTPS)."); return; }
      try {
        const previa = camRef.current; camRef.current = null;
        let stream;
        try { stream = await (previa || pedirCamara()); } catch (e) { if (!previa) throw e; stream = await pedirCamara(); }   // si falló el precalentado, un intento normal
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        await video.play();
        if (!cancelado) setCamLista(true);
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        setCamError(e?.name === "NotAllowedError" ? "Permiso de cámara denegado. Actívalo en los ajustes del navegador para este sitio."
          : e?.name === "NotFoundError" ? "No se ha encontrado ninguna cámara." : "No se pudo acceder a la cámara.");
      }
    })();
    return () => { cancelado = true; pararCamara(); };
  }, [abierto, fase, enviar, pararCamara]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { clearTimeout(cierreRef.current); pararCamara(); }, [pararCamara]);

  if (!activo) return null;

  const alertas = resultado && fase === "confirmar" ? TEXTOS_ALERTA(resultado) : [];
  const hayRojo = alertas.some((a) => a.c === "rojo");
  const geoTxt = geo.estado === "ok" ? `📍 Ubicación obtenida (±${geo.acc} m)` : geo.estado === "buscando" ? "📍 Cargando ubicación… espera unos segundos"
    : geo.estado === "denegada" ? "📍 Ubicación denegada en el navegador: actívala para fichar con ubicación" : "";

  return (
    <>
      {enPiso && !sinBarra && <div style={st.tinte} aria-hidden="true" />}
      {sinBarra ? null : enPiso ? (
        <div ref={barraRef} style={st.barra}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 900, fontSize: 22, lineHeight: 1.2, wordBreak: "break-word" }}>🟢 EN {enPiso.abreviatura}</div>
            <div style={{ fontSize: 15, fontWeight: 700, opacity: 0.95, marginTop: 2 }}>desde las {hhmm(enPiso.desde)} · lleva {fmtDur(minDesde(enPiso.desde))}</div>
          </div>
          <button onClick={abrir} style={st.btnSalida}>📷 Fichar<br />salida</button>
        </div>
      ) : (
        !abierto && ROLES_CON_BOTON_FLOTANTE.includes(user.tipo) && <button onClick={abrir} style={st.fab}>📷 Fichar</button>
      )}

      {abierto && (
        <div style={st.overlay}>
          <div style={st.modal}>
            {fase === "escaneando" && (
              <>
                <div style={st.cab}>
                  <strong style={{ fontSize: 17 }}>{enPiso ? `📷 Escanea el QR de ${enPiso.abreviatura} para salir` : "📷 Escanea el QR del piso"}</strong>
                  <button onClick={cerrar} style={st.cerrarX} aria-label="Cerrar">✕</button>
                </div>
                {camError ? <div style={st.cajaRoja}>{camError}</div> : (
                  <div style={{ ...st.videoWrap, position: "relative" }}><video ref={videoRef} style={st.video} muted playsInline /><canvas ref={canvasRef} style={{ display: "none" }} />{!camLista && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 16 }}>📷 Abriendo cámara…</div>}</div>
                )}
                <div style={{ fontSize: 13, color: geo.estado === "ok" ? "#166534" : geo.estado === "buscando" ? "#475569" : "#b45309", marginTop: 8 }}>{geoTxt}</div>
                {geo.estado === "denegada" && <button onClick={pedirUbicacion} style={st.btnSec}>Reintentar ubicación</button>}
                {enPiso && <button onClick={abrirManual} style={st.btnOlvido}>⏰ Se me olvidó desfichar</button>}
              </>
            )}

            {fase === "desactivado" && (() => {
              const que = [permisoDesactivado("ubicacion") && "la ubicación", permisoDesactivado("camara") && "la cámara"].filter(Boolean).join(" y ");
              return (
                <div style={{ ...st.centro, background: "#fee2e2", borderRadius: 16, padding: 24 }}>
                  <div style={{ fontSize: 50 }}>🚫</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: "#991b1b" }}>No puedes fichar</div>
                  <div style={{ fontSize: 15, color: "#7f1d1d", fontWeight: 600, margin: "8px 0 14px" }}>Has desactivado {que}. Actívala para poder fichar (también en 🔔 › Permisos).</div>
                  <button onClick={() => { setPermisoDesactivado("ubicacion", false); setPermisoDesactivado("camara", false); abrirRef.current(); }} style={{ width: "100%", border: "none", borderRadius: 12, padding: "13px 0", background: "#16a34a", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer", marginBottom: 8 }}>Activar y fichar</button>
                  <button onClick={cerrar} style={st.btnSec}>Cerrar</button>
                </div>
              );
            })()}

            {fase === "manual" && enPiso && horaSel && (
              <div>
                <div style={{ ...st.alertaCab, background: "#dc2626" }}>⏰ Se me olvidó desfichar</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}>Estás en {enPiso.abreviatura}</div>
                <div style={st.entroA}>Entraste {nombreDia(enPiso.desde.slice(0, 10))} a las <b>{hhmm(enPiso.desde)}</b></div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#991b1b", margin: "10px 0 8px" }}>¿A qué hora saliste?</div>
                <SelectorHora desde={enPiso.desde} valor={horaSel} onChange={setHoraSel} />
                <div style={{ fontSize: 13, color: geo.estado === "ok" ? "#166534" : "#b45309", marginTop: 8 }}>{geo.estado === "ok" ? "📍 Se guardará también dónde estás ahora" : geo.estado === "denegada" ? geoTxt : "📍 Buscando tu ubicación…"}</div>
                {errManual && <div style={{ ...st.cajaRoja, marginTop: 10 }}>{errManual}</div>}
                <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                  <button onClick={cerrar} style={st.btnSec}>Cancelar</button>
                  <button onClick={guardarManual} disabled={!horaValida(enPiso.desde, horaSel)} style={{ ...st.btnConfirmar, background: "#dc2626", opacity: horaValida(enPiso.desde, horaSel) ? 1 : 0.5 }}>Guardar salida</button>
                </div>
              </div>
            )}

            {fase === "enviando" && <div style={st.centro}><div style={{ fontSize: 40 }}>⏳</div><div style={{ fontWeight: 700 }}>Fichando…</div>{cargandoUbic && <div style={{ marginTop: 10, fontWeight: 800, color: "#1d4ed8", fontSize: 16 }}>📍 Cargando ubicación… espera unos segundos</div>}</div>}

            {fase === "ok" && resultado && (() => {
              const sinCoord = resultado.estado === "piso_sin_coordenadas";
              const alerta = resultado.estado === "fuera" || resultado.estado === "qr_desconocido";
              return (
                <div style={{ ...st.centro, background: alerta ? "#fef3c7" : "#dcfce7", borderRadius: 16, padding: 24 }}>
                  <div style={{ fontSize: 54 }}>{resultado.tipo === "salida" ? "👋" : "✅"}</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: "#14532d" }}>{resultado.tipo === "salida" ? (resultado.manual ? "Salida manual guardada" : "Salida fichada") : "Entrada fichada"}</div>
                  <div style={{ fontSize: 17, fontWeight: 700, marginTop: 4 }}>{resultado.piso?.abreviatura || "QR no reconocido"}</div>
                  {resultado.tipo === "salida" && resultado.duracion_min != null && (
                    <div style={{ marginTop: 12, background: "#fff", border: "3px solid #16a34a", borderRadius: 16, padding: "12px 10px" }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#166534", textTransform: "uppercase", letterSpacing: "0.5px" }}>⏱ Has estado</div>
                      <div style={{ fontSize: 42, fontWeight: 900, color: "#14532d", lineHeight: 1.1 }}>{fmtDur(resultado.duracion_min)}</div>
                    </div>
                  )}
                  {resultado.distancia_m != null && <div style={{ fontSize: 13, color: "#166534", marginTop: 4 }}>A {Math.round(resultado.distancia_m)} m del piso</div>}
                  {sinCoord && <div style={st.avisoAmbar}>No se ha podido comprobar la ubicación (piso sin coordenadas)</div>}
                  {resultado.aviso_no_asociado && <div style={st.avisoAmbar}>⚠️ Este piso no está asociado a ti</div>}
                </div>
              );
            })()}

            {fase === "confirmar" && resultado && (
              <div>
                <div style={{ ...st.alertaCab, background: hayRojo ? "#dc2626" : "#d97706" }}>⚠️ {resultado.tipo === "salida" ? "Salida" : "Entrada"}{resultado.piso ? ` · ${resultado.piso.abreviatura}` : ""}</div>
                {alertas.map((a, i) => (
                  <div key={i} style={{ ...st.alertaCaja, background: a.c === "rojo" ? "#fee2e2" : "#fef3c7", borderColor: a.c === "rojo" ? "#fca5a5" : "#fcd34d", color: a.c === "rojo" ? "#991b1b" : "#92400e" }}>
                    <div style={{ fontWeight: 900, fontSize: 16 }}>{a.t}</div>{a.d && <div style={{ fontSize: 14, marginTop: 2 }}>{a.d}</div>}
                  </div>
                ))}
                {resultado.cambio_de_piso && resultado.anterior_desde && horaSel && (
                  <div style={{ margin: "10px 0 0" }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#92400e", marginBottom: 8 }}>Hora aproximada de salida de {resultado.piso_anterior?.abreviatura}</div>
                    <SelectorHora desde={resultado.anterior_desde} valor={horaSel} onChange={setHoraSel} />
                  </div>
                )}
                {(() => {
                  const bloqueado = resultado.cambio_de_piso && resultado.anterior_desde && !horaValida(resultado.anterior_desde, horaSel);
                  return (
                    <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                      <button onClick={cerrar} style={st.btnSec}>Cancelar</button>
                      <button onClick={() => enviar(qrRef.current, true, resultado.cambio_de_piso && horaSel ? valorAIso(horaSel) : null)} disabled={bloqueado}
                        style={{ ...st.btnConfirmar, background: hayRojo ? "#dc2626" : "#d97706", opacity: bloqueado ? 0.5 : 1 }}>Confirmar fichaje</button>
                    </div>
                  );
                })()}
              </div>
            )}

            {fase === "error" && (
              <div>
                <div style={st.cajaRoja}>{resultado?.mensaje || "No se pudo fichar."}</div>
                <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                  <button onClick={cerrar} style={st.btnSec}>Cerrar</button>
                  <button onClick={() => { setResultado(null); setFase("escaneando"); }} style={st.btnConfirmar}>Reintentar</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const st = {
  tinte: { position: "fixed", inset: 0, background: "rgba(34,197,94,0.13)", pointerEvents: "none", zIndex: 8900 },
  barra: { position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 9000, background: "#15803d", color: "#fff", display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", boxShadow: "0 -3px 14px rgba(0,0,0,0.25)" },
  btnSalida: { border: "none", borderRadius: 10, padding: "9px 14px", lineHeight: 1.2, textAlign: "center", fontWeight: 900, fontSize: 16, background: "#fff", color: "#15803d", cursor: "pointer", whiteSpace: "nowrap" },
  fab: { position: "fixed", right: 14, bottom: 14, zIndex: 9000, border: "none", borderRadius: 999, padding: "13px 20px", fontWeight: 900, fontSize: 15, background: "#2563eb", color: "#fff", boxShadow: "0 6px 18px rgba(37,99,235,0.45)", cursor: "pointer" },
  overlay: { position: "fixed", inset: 0, zIndex: 9500, background: "rgba(15,23,42,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 14 },
  modal: { background: "#fff", borderRadius: 18, width: "min(440px, 100%)", maxHeight: "92vh", overflowY: "auto", padding: 16, boxShadow: "0 20px 50px rgba(0,0,0,0.35)", fontFamily: "Segoe UI, sans-serif" },
  cab: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 },
  cerrarX: { marginLeft: "auto", border: "none", background: "#f1f5f9", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 800 },
  videoWrap: { borderRadius: 14, overflow: "hidden", background: "#000", aspectRatio: "1 / 1" },
  video: { width: "100%", height: "100%", objectFit: "cover" },
  cajaRoja: { background: "#fee2e2", border: "1px solid #fca5a5", color: "#991b1b", borderRadius: 12, padding: 12, fontSize: 14 },
  centro: { textAlign: "center", padding: "26px 10px" },
  avisoAmbar: { marginTop: 10, background: "#fef3c7", border: "1px solid #fcd34d", color: "#92400e", borderRadius: 10, padding: "8px 10px", fontWeight: 800, fontSize: 14 },
  alertaCab: { color: "#fff", fontWeight: 900, fontSize: 17, borderRadius: 12, padding: "10px 14px", marginBottom: 10 },
  alertaCaja: { border: "2px solid", borderRadius: 12, padding: "10px 12px", marginBottom: 8 },
  btnSec: { flex: 1, border: "2px solid #cbd5e1", background: "#fff", color: "#334155", borderRadius: 12, padding: "12px 14px", fontWeight: 800, fontSize: 15, cursor: "pointer", marginTop: 6 },
  btnOlvido: { display: "block", width: "100%", marginTop: 12, border: "none", borderRadius: 12, padding: "14px 12px", background: "#dc2626", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer", boxShadow: "0 3px 10px rgba(220,38,38,0.35)" },
  entroA: { marginTop: 4, background: "#f1f5f9", borderRadius: 10, padding: "8px 12px", fontSize: 16, color: "#334155" },
  selDia: { width: "100%", boxSizing: "border-box", fontSize: 17, fontWeight: 800, padding: "10px 12px", borderRadius: 12, border: "2px solid #cbd5e1", background: "#fff", color: "#1e293b", marginBottom: 10 },
  relojFila: { display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 10 },
  relojCol: { flex: 1, textAlign: "center" },
  relojLbl: { fontSize: 13, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 },
  selHora: { width: "100%", boxSizing: "border-box", fontSize: 34, fontWeight: 900, textAlign: "center", textAlignLast: "center", padding: "10px 4px", borderRadius: 14, border: "3px solid #1e3a5f", background: "#fff", color: "#1e3a5f" },
  relojPuntos: { fontSize: 38, fontWeight: 900, color: "#1e3a5f", paddingBottom: 10 },
  relojResumen: { marginTop: 10, textAlign: "center", fontSize: 15, color: "#334155", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "8px 10px" },
  btnConfirmar: { flex: 1.4, border: "none", color: "#fff", background: "#2563eb", borderRadius: 12, padding: "12px 14px", fontWeight: 900, fontSize: 15, cursor: "pointer", marginTop: 6 },
};

export default FichajePiso;
